import React, { useState } from 'react';
import { ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';
import { fmtMoney0 } from '../../utils/currency';

const fmt = fmtMoney0;

/**
 * Pure SVG sparkline — no chart library needed.
 * Draws a filled area chart from an array of numbers.
 */
function Sparkline({ data, color = '#2563EB', height = 48, width = 240 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padY = 4;
  const chartH = height - padY * 2;
  const step = width / (data.length - 1);

  const points = data.map((v, i) => {
    const x = i * step;
    const y = padY + chartH - ((v - min) / range) * chartH;
    return `${x},${y}`;
  });

  // Filled area (bottom line + polyline)
  const areaPoints = `0,${height} ${points.join(' ')} ${width},${height}`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`cf-grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.03" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#cf-grad-${color.replace('#', '')})`} />
      <polyline points={points.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * CASH FLOW MINI-CHART — compact sparkline panel for the status sidebar.
 * Derives monthly cash flow (revenue − expenses) from the trend data
 * already fetched by useDashboardData.
 */
export default function CashFlowMiniChart({ salesTrend = [], expenseTrend = [] }) {
  const [open, setOpen] = useState(true);

  // Merge trends by month
  const months = [...new Set([...salesTrend.map(s => s.month), ...expenseTrend.map(e => e.month)])].sort();
  const cashFlowData = months.map(m => {
    const sale = salesTrend.find(s => s.month === m);
    const exp = expenseTrend.find(e => e.month === m);
    return parseFloat(sale?.revenue || 0) - parseFloat(exp?.total_expenses || 0);
  });

  const monthLabels = months.map(m => {
    const [y, mo] = m.split('-');
    return new Date(y, parseInt(mo, 10) - 1).toLocaleString('en-US', { month: 'short' });
  });

  // Totals
  const totalIn = salesTrend.reduce((s, t) => s + parseFloat(t.revenue || 0), 0);
  const totalOut = expenseTrend.reduce((s, t) => s + parseFloat(t.total_expenses || 0), 0);
  const net = totalIn - totalOut;
  const isPositive = net >= 0;

  // Determine color based on trend
  const color = isPositive ? '#16A34A' : '#DC2626';

  if (cashFlowData.length < 2) {
    return (
      <section className="desk-status-section">
        <button type="button" className="desk-status-toggle" onClick={() => setOpen(o => !o)}>
          <ChevronDown /> Cash Flow
        </button>
        {open && (
          <div className="desk-status-body">
            <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 11, color: 'var(--desk-text-3)' }}>
              Not enough data yet
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="desk-status-section">
      <button type="button" className={`desk-status-toggle ${open ? '' : 'collapsed'}`} onClick={() => setOpen(o => !o)}>
        <ChevronDown /> Cash Flow
      </button>
      {open && (
        <div className="desk-status-body">
          <div className="cf-summary">
            <div className={`cf-net ${isPositive ? 'positive' : 'negative'}`}>
              {isPositive ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              <span>{fmt(net)}</span>
            </div>
            <div className="cf-label">Net Cash Flow</div>
          </div>

          <div className="cf-chart">
            <Sparkline data={cashFlowData} color={color} height={44} />
          </div>

          <div className="cf-bars">
            {cashFlowData.map((v, i) => (
              <div key={i} className="cf-bar-col" title={`${monthLabels[i]}: ${fmt(v)}`}>
                <div className={`cf-bar ${v >= 0 ? 'pos' : 'neg'}`}
                  style={{ height: `${Math.min(100, Math.abs(v) / (Math.max(...cashFlowData.map(Math.abs)) || 1) * 100)}%` }} />
                <span className="cf-bar-label">{monthLabels[i]}</span>
              </div>
            ))}
          </div>

          <div className="cf-detail">
            <div className="cf-detail-row">
              <span className="cf-detail-label">Inflows</span>
              <span className="cf-detail-value inflow">{fmt(totalIn)}</span>
            </div>
            <div className="cf-detail-row">
              <span className="cf-detail-label">Outflows</span>
              <span className="cf-detail-value outflow">{fmt(totalOut)}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
